import { AlertTriangle, Clock3, RefreshCw, ShieldAlert } from "lucide-react";
import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";

import { DefinitionRow, Metric, SignalList } from "../components/data-display";
import { DashboardLoading, EmptyState, ErrorState, PageHeader } from "../components/page-state";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useDashboard } from "../hooks/use-dashboard";
import { formatDateTime, formatEth, formatPercent, formatSignedUsd, formatUsd, pnlTone } from "../lib/format";

const PriceSparkline = lazy(() => import("../components/price-sparkline").then((module) => ({ default: module.PriceSparkline })));

const positionStatus = {
  "pending-entry": "Entry pending",
  open: "Position open",
  "pending-exit": "Exit pending",
};

export function LiveTradePage() {
  const { dashboard, loading, error, refresh } = useDashboard();
  if (loading && !dashboard) return <DashboardLoading />;
  if (!dashboard) return <ErrorState message={error ?? "No dashboard snapshot was returned."} onRetry={() => void refresh()} />;

  const { market, position, signals, fees, strategy } = dashboard;

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="ETH/USD"
        title={position ? "One position, fully explained." : "Watching for a valid setup."}
        description={position ? "Live market context, entry logic, target, stop, and the expected result after fees." : "No order will be placed until every required signal and fee check is ready."}
        actions={
          <Button variant="outline" size="sm" onClick={() => void refresh()}><RefreshCw data-icon="inline-start" /> Refresh</Button>
        }
      />

      {market.stale && (
        <div className="flex items-start gap-3 rounded-xl border border-coral/25 bg-coral-soft px-4 py-3 text-sm text-destructive" role="alert">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p><strong>Market data is stale.</strong> New live entries should remain disabled until the feed recovers.</p>
        </div>
      )}

      <section className="grid gap-7 border-b border-border pb-8 xl:grid-cols-[minmax(0,1.55fr)_minmax(18rem,0.45fr)]" aria-labelledby="market-heading">
        <div>
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-muted-foreground">MARKET PATH</p>
              <h2 id="market-heading" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">ETH/USD</h2>
            </div>
            <div className="text-right">
              <p className="tabular-nums font-display text-3xl font-bold tracking-[-0.04em]">{formatUsd(market.price)}</p>
              <p className={pnlTone(market.change24hPercent) === "positive" ? "mt-1 text-xs font-semibold text-positive" : pnlTone(market.change24hPercent) === "negative" ? "mt-1 text-xs font-semibold text-destructive" : "mt-1 text-xs text-muted-foreground"}>
                {formatPercent(market.change24hPercent, true)} over 24h
              </p>
            </div>
          </div>
          <Suspense fallback={<div className="h-44 animate-pulse rounded-xl bg-muted" aria-label="Loading market chart" />}>
            <PriceSparkline data={market.sparkline} />
          </Suspense>
          <p className="mt-3 text-xs text-muted-foreground">Last market update: {formatDateTime(market.updatedAt)} · Status: {market.systemStatus}</p>
        </div>
        <dl className="rounded-2xl border border-border bg-card px-5 py-2">
          <DefinitionRow label="Best bid" value={formatUsd(market.bid)} />
          <DefinitionRow label="Best ask" value={formatUsd(market.ask)} />
          <DefinitionRow label="24h range" value={formatPercent(market.range24hPercent)} />
          <DefinitionRow label="Maker fee" value={formatPercent(fees.makerPercent)} />
          <DefinitionRow label="Taker fee" value={formatPercent(fees.takerPercent)} />
          <DefinitionRow label="Fee source" value={fees.source === "authenticated" ? "Kraken account" : "Unavailable"} />
        </dl>
      </section>

      {position ? (
        <section className="grid gap-7 xl:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]" aria-labelledby="position-title">
          <div>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-muted-foreground">POSITION</p>
                <h2 id="position-title" className="font-display mt-1 text-2xl font-bold tracking-[-0.035em]">{positionStatus[position.status]}</h2>
              </div>
              <div className="flex items-center gap-2"><Badge variant={position.mode === "live" ? "outline" : "secondary"}>{position.mode}</Badge><Badge variant="outline">Long ETH</Badge></div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-7 border-y border-border py-6 sm:grid-cols-4">
              <Metric label="Quantity" value={formatEth(position.quantityEth)} />
              <Metric label="Entry" value={formatUsd(position.entryPrice)} />
              <Metric label="Current" value={formatUsd(position.currentPrice)} />
              <Metric label="Estimated net" value={formatSignedUsd(position.estimatedNetPnlUsd)} tone={pnlTone(position.estimatedNetPnlUsd)} />
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="text-xs font-bold text-muted-foreground">EXIT PLAN</p>
                <dl className="mt-2">
                  <DefinitionRow label="Target" value={formatUsd(position.targetPrice)} emphasis />
                  <DefinitionRow label="Protective stop" value={formatUsd(position.stopPrice)} emphasis />
                  <DefinitionRow label="Maximum hold" value={`${strategy.maxHoldMinutes} minutes`} />
                </dl>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="text-xs font-bold text-muted-foreground">FEE-ADJUSTED ESTIMATE</p>
                <dl className="mt-2">
                  <DefinitionRow label="Gross P/L" value={formatSignedUsd(position.grossPnlUsd)} />
                  <DefinitionRow label="Estimated fees" value={formatUsd(position.estimatedFeesUsd)} />
                  <DefinitionRow label="Estimated net" value={formatSignedUsd(position.estimatedNetPnlUsd)} emphasis />
                </dl>
              </div>
            </div>
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-muted px-4 py-3 text-sm leading-6 text-muted-foreground">
              <Clock3 className="mt-1 size-4 shrink-0 text-foreground" />
              <p>Opened {formatDateTime(position.openedAt)}. Estimates are not guarantees; final execution price, fees, and slippage may differ.</p>
            </div>
          </div>
          <aside>
            <p className="text-xs font-bold text-muted-foreground">WHY THIS TRADE EXISTS</p>
            <p className="mt-2 text-sm leading-6 text-foreground">{position.reason}</p>
            <div className="mt-5"><SignalList signals={signals} /></div>
          </aside>
        </section>
      ) : (
        <div className="grid gap-8 xl:grid-cols-[minmax(0,1.05fr)_minmax(20rem,0.95fr)]">
          <EmptyState
            title="No valid setup right now"
            description="Your funds are not currently in an ETH/USD position. The bot is waiting for a setup that clears its trend, pullback, liquidity, and fee checks."
            action={<Button variant="outline" asChild><Link to="/strategy">Review strategy</Link></Button>}
          />
          <section aria-labelledby="checks-title">
            <div className="flex items-center gap-2">
              <ShieldAlert className="size-4 text-muted-foreground" />
              <h2 id="checks-title" className="font-display text-xl font-bold tracking-[-0.03em]">Entry readiness</h2>
            </div>
            <SignalList signals={signals} />
          </section>
        </div>
      )}
    </div>
  );
}
